import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Button, Form, FormFeedback, FormGroup, FormText, Input, Label,
} from 'reactstrap';
import isEmail from 'validator/es/lib/isEmail';

import PasswordResetConfirmMutation from 'src/mutations/PasswordResetConfirmMutation';
import FlashesStore from 'src/stores/FlashesStore';
import isPassword from 'src/validators/isPassword';

import AuthContainer, { AUTH_TYPE_RESET_PASSWORD } from '../AuthContainer';

class AuthResetPassword extends React.Component {
  static mutationName = 'authResetPassword';

  constructor(props) {
    super(props);

    this.state = {
      email: '',
      emailValid: null,
      password: '',
      passwordValid: null,
      confirmPassword: '',
      confirmPasswordValid: null,
      processing: false,
    };
  }

  handleEmailChange = (e) => {
    const { value: email } = e.target;
    const emailValid = isEmail(email);

    this.setState({ email, emailValid });
  };

  handlePasswordChange = (e) => {
    const { value: password } = e.target;
    const passwordValid = isPassword(password);

    this.setState({ password, passwordValid });
  };

  handleConfirmPasswordChange = (e) => {
    const { value: confirmPassword } = e.target;
    const { password } = this.state;
    const confirmPasswordValid = confirmPassword === password;

    this.setState({ confirmPassword, confirmPasswordValid });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { email, password, processing } = this.state;
    const { intl } = this.props;

    FlashesStore.reset();

    if (processing) {
      const msgInprogress = intl.formatMessage({
        id: 'auth.auth_reset_password.form.state.invalid_still_processing',
        defaultMessage: 'We are still processing your request...',
      });
      FlashesStore.flash(FlashesStore.INFO, msgInprogress, AuthResetPassword.mutationName);
      return;
    }

    if (!this.isValid()) {
      const msgInvalidData = intl.formatMessage({ id: 'auth.auth_reset_password.form.state.invalid_data', defaultMessage: 'Form data not valid. Please see below.' });
      FlashesStore.flash(FlashesStore.ERROR, msgInvalidData, AuthResetPassword.mutationName);
      return;
    }

    this.setState({ processing: true });

    const { match } = this.props;
    const { params } = match;
    const { token } = params;

    PasswordResetConfirmMutation(
      {
        email,
        password,
        verificationCode: token,
      },
      this.handleSubmitSuccess,
      this.handleSubmitFailure,
    );
  };

  // eslint-disable-next-line no-unused-vars
  handleSubmitSuccess = (_response) => {
    this.setState({ processing: false });

    const { intl } = this.props;
    const msgSuccess = intl.formatMessage({
      id: 'auth.auth_reset_password.form.response_message.success',
      defaultMessage: 'Password reset was successful. Please confirm by logging in with your email and password.',
    });
    FlashesStore.flash(
      FlashesStore.SUCCESS,
      msgSuccess,
      AuthResetPassword.mutationName,
    );

    const { router } = this.props;
    router.push('/login');
  };

  handleSubmitFailure = (error) => {
    this.setState({ processing: false });

    FlashesStore.flash(FlashesStore.ERROR, error.message, AuthResetPassword.mutationName);
  };

  isValid = () => {
    const { emailValid, passwordValid, confirmPasswordValid } = this.state;

    return emailValid && passwordValid && confirmPasswordValid;
  };

  render() {
    const {
      email, emailValid, password, passwordValid,
      confirmPassword, confirmPasswordValid, processing,
    } = this.state;
    const { intl } = this.props;

    const pageTitle = intl.formatMessage({ id: 'auth.auth_reset_password.page.title', defaultMessage: 'Enosi - set up a new password' });
    const formTitle = intl.formatMessage({ id: 'auth.auth_reset_password.form.title', defaultMessage: 'Set a new password' });
    const emailLabel = intl.formatMessage({ id: 'auth.auth_reset_password.form.email.label', defaultMessage: 'Your account’s email address' });
    const emailPlaceholder = intl.formatMessage({ id: 'auth.auth_reset_password.form.email.placeholder', defaultMessage: 'Your account’s email address' });
    const emailFeedbackInvalid = intl.formatMessage({ id: 'auth.auth_reset_password.form.email.form_feedback.invalid', defaultMessage: 'Invalid email address' });
    const emailHelpText = intl.formatMessage({ id: 'auth.auth_reset_password.form.email.help_text', defaultMessage: 'Email address associated with your Enosi account' });
    const passwordLabel = intl.formatMessage({ id: 'auth.auth_reset_password.form.password.label', defaultMessage: 'Password' });
    const passwordPlaceholder = intl.formatMessage({ id: 'auth.auth_reset_password.form.password.placeholder', defaultMessage: 'Enter password' });
    const passwordFeedbackInvalid = intl.formatMessage({ id: 'auth.auth_reset_password.form.password.form_feedback.invalid', defaultMessage: 'Passwords must match' });
    const passwordHelpText = intl.formatMessage({ id: 'auth.auth_reset_password.form.password.help_text', defaultMessage: 'Confirm the password you are setting' });
    const confirmPasswordLabel = intl.formatMessage({ id: 'auth.auth_reset_password.form.confirm_password.label', defaultMessage: 'Password confirmation' });
    const confirmPasswordPlaceholder = intl.formatMessage({ id: 'auth.auth_reset_password.form.confirm_password.placeholder', defaultMessage: 'Confirm password' });
    const submitLabel = intl.formatMessage({ id: 'auth.auth_reset_password.form.submit.label', defaultMessage: 'Update' });

    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{pageTitle}</title>
        </Helmet>

        <AuthContainer type={AUTH_TYPE_RESET_PASSWORD} title={formTitle}>
          <Form onSubmit={this.handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">{emailLabel}</Label>
              <Input
                type="email"
                id="email"
                aria-describedby="email"
                placeholder={emailPlaceholder}
                onChange={this.handleEmailChange}
                value={email}
                disabled={processing}
                valid={emailValid}
                invalid={emailValid !== null && !emailValid}
              />
              <FormFeedback>{emailFeedbackInvalid}</FormFeedback>
              <FormText>{emailHelpText}</FormText>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="formPassword">{passwordLabel}</Label>
              <Input
                type="password"
                id="formPassword"
                aria-describedby="password"
                placeholder={passwordPlaceholder}
                onChange={this.handlePasswordChange}
                value={password}
                valid={passwordValid}
                invalid={passwordValid !== null && !passwordValid}
                disabled={processing}
              />
              <FormFeedback>
                <FormattedMessage
                  id="auth.auth_login.form.password.form_feedback.invalid"
                  defaultMessage="Password must be at least 8 characters long, with uppercase, lowercase, number and one of:{specialCharacters}"
                  values={{
                    specialCharacters:
                      // eslint-disable-next-line react/jsx-indent
                      <>
                        <br />
                        <code className="code">
                          {'^ $ * . [ ] { } ( ) ? " ! @ # % & / \\ , < > \' : ; | _ ~ `'}
                        </code>
                      </>,
                  }}
                />
              </FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="formPasswordConfirmation">
                {confirmPasswordLabel}
              </Label>
              <Input
                type="password"
                id="formPasswordConfirmation"
                aria-describedby="password"
                placeholder={confirmPasswordPlaceholder}
                onChange={this.handleConfirmPasswordChange}
                value={confirmPassword}
                valid={confirmPasswordValid}
                invalid={confirmPasswordValid !== null && !confirmPasswordValid}
                disabled={processing}
              />
              <FormFeedback>{passwordFeedbackInvalid}</FormFeedback>
              <FormText>{passwordHelpText}</FormText>
            </FormGroup>
            <Button type="submit" color="primary">
              {submitLabel}
            </Button>
          </Form>
        </AuthContainer>
      </>
    );
  }
}

AuthResetPassword.propTypes = {
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default injectIntl(AuthResetPassword);
